// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import Page from "@insite/mobius/Page";
import clearReorderPadSearchParams from "@steiner/App/Handlers/ReorderPad/ClearReorderPadSearchParams";
import loadReorderPad, { defaultReorderPadParameters } from "@steiner/App/Handlers/ReorderPad/LoadReorderPad";
import { CustomAppState, ReorderPadItemModel } from "@steiner/App/Store/Reducers";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    return {
        reorderPadOrders: state.xReorderPad.orders,
        isLoading: state.xReorderPad.isLoading,
        pagination: state.xReorderPad.pagination,
        searchParams: state.xReorderPad.searchParams,
    };
};

const mapDispatchToProps = {
    loadReorderPad,
    clearReorderPadSearchParams,
    loadProductInfoList,
};
type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const ReorderPadPageDataContext = React.createContext<{
    orders: ReorderPadItemModel[] | undefined;
}>({
    orders: [],
});

const ReorderPadPage = ({ id, reorderPadOrders, isLoading, pagination, searchParams, loadReorderPad, clearReorderPadSearchParams }: Props) => {
    useEffect(() => {
        if (!isLoading) {
            // XNG-Changes - STR-2548 - reset the filters when the page is loaded
            clearReorderPadSearchParams({});
        }
    }, []);

    React.useEffect(() => {
        if (reorderPadOrders && !isLoading) {
           const timeoutId = setTimeout(() => {
            loadReorderPad({
                ...defaultReorderPadParameters,
                ...searchParams,
                pageSize: pagination?.pageSize || pagination?.defaultPageSize || 16,
            });
           }, 1000);

           return () => clearTimeout(timeoutId);
        }
    }, [searchParams]);

    React.useEffect(() => {
        if (!reorderPadOrders && !isLoading) {
            loadReorderPad({ ...defaultReorderPadParameters, pageSize: 16 });
        }
    }, [reorderPadOrders, isLoading]);

    return (
        <Page data-test-selector="reorderPad">
            <ReorderPadPageDataContext.Provider value={{ orders: reorderPadOrders }}>
                <Zone contentId={id} zoneName="Content" />
                <Modals />
            </ReorderPadPageDataContext.Provider>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReorderPadPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "ReorderPadPage" instead of this constant.
 */
export const ReorderPadPageContext = "ReorderPadPage";
